import React from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

import { cn } from "../../../common/u1/cn";
import { FieldLabel } from "../../../common/u1/FieldLabel";
import { SelectField } from "../../../common/u1/SelectField";
import { ReadOnlyField } from "../../../common/u1/ReadOnlyField";
import { EditFooterBar } from "./EditFooterBar";
import { EditSectionLayout } from "./EditSectionLayout";
import { VerifiedFieldNote } from "./EditNotes";
import { SectionStatusPill } from "./SectionStatusPill";
import { useAboutSection, EMPLOYEE_OPTIONS } from "./useAboutSection";

/* Section 01 — About Company. */

/* Transcribed from About.jsx: the paste override that keeps pasted rich text
   out of the description. Registered once at module scope, as the live
   component does it on every render. */
const Clipboard = ReactQuill.Quill.import("modules/clipboard");
class PlainClipboard extends Clipboard {
  onPaste(e) {
    e.preventDefault();
    const text = e.clipboardData.getData("text/plain");
    const range = this.quill.getSelection();
    this.quill.insertText(range.index, text);
  }
}
ReactQuill.Quill.register("modules/clipboard", PlainClipboard, true);

const CARD =
  "rounded-[14px] border border-[var(--line)] bg-white px-4 py-5 sm:px-6 sm:py-6";

export const AboutCompanySection = ({ onNext, onSkip, header, className = "" }) => {
  const {
    companyName,
    employees,
    setEmployees,
    description,
    onDescriptionChange,
    displayStatus,
    isLoading,
    submit,
  } = useAboutSection({ onSaved: onNext });

  return (
    <EditSectionLayout
      className={className}
      header={header}
      footer={
        /* First section of the flow — nothing to go back to, which is why the
           live About.jsx renders no Back button either. */
        <EditFooterBar onSkip={onSkip} onContinue={submit} isLoading={isLoading} />
      }
    >
      <div className="flex items-center gap-2.5">
        <h2 className="m-0 text-[16px] font-bold text-[var(--ink)] font-sans">
          About Company
        </h2>
        {/* Not in the design, but the live heading carries an approval glyph
            and dropping it would hide where an edit has got to. */}
        <SectionStatusPill status={displayStatus} />
      </div>

      <div className={cn(CARD, "mt-3.5")}>
        <div>
          <ReadOnlyField
            label="Company Name"
            labelVariant="field"
            value={companyName || "—"}
          />
          <VerifiedFieldNote />
        </div>

        <SelectField
          className="mt-5 max-w-[420px]"
          label="Number of Employees"
          labelVariant="field"
          id="u1-employees"
          value={employees || ""}
          onChange={(e) => setEmployees(e.target.value)}
          options={EMPLOYEE_OPTIONS}
          placeholder="Select"
        />

        <div className="mt-5">
          {/* The editor is not a labellable element, so this is a span rather
              than a <label> pointing at nothing. */}
          <FieldLabel as="span" variant="field">
            Description
          </FieldLabel>
          {/* The border, radius and focus ring live on this wrapper; the
              editor's own snow chrome is stripped in tokens.css so it does not
              read as a second field inside the card. */}
          <div className="u1-quill rounded-lg border border-[var(--field-border)] bg-white transition-[border-color,box-shadow] duration-150">
            <ReactQuill
              value={description || ""}
              onChange={onDescriptionChange}
              modules={{ clipboard: { matchVisual: false } }}
            />
          </div>
        </div>
      </div>

    </EditSectionLayout>
  );
};

export default AboutCompanySection;
